/**
 * Cluster List — sortable/filterable table of all unserved church clusters.
 *
 * Consumes GET /api/admin/dashboard/analytics/church-demand/clusters via
 * churchDemandAPI.clusters(params). Backend handles sort/filter/pagination;
 * the "Needs a Glance" filter is the one exception — it runs client-side on
 * the currently-loaded page because it's an OR condition the backend doesn't
 * support natively.
 *
 * Row click → navigate to /church-demand/clusters/:clusterId (Task 5.6 fills
 * in the detail page).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { churchDemandAPI } from '../../../services/api';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
} from '@heroicons/react/24/outline';

const PAGE_SIZE = 50;

const TRUST_LEVELS = [
  { value: '', label: 'All' },
  { value: 'auto_high', label: 'Auto High' },
  { value: 'auto_medium', label: 'Auto Medium' },
  { value: 'auto_low', label: 'Auto Low' },
  { value: 'human_confirmed', label: 'Confirmed' },
  { value: 'human_overridden', label: 'Overridden' },
];

// Column definitions drive both the header (click-to-sort) and the body cells.
// `sortKey` matches the backend whitelist in routes/admin/analytics/church-demand.js.
const COLUMNS = [
  { key: 'name', label: 'Name', align: 'left', sortKey: 'canonical_name' },
  { key: 'denomination', label: 'Denomination', align: 'left', sortKey: 'denomination' },
  { key: 'location', label: 'Location', align: 'left', sortKey: 'state' },
  { key: 'signals', label: 'Signals', align: 'right', sortKey: 'signal_count' },
  { key: 'users', label: 'Users', align: 'right', sortKey: 'unique_user_count' },
  { key: 'trust', label: 'Trust', align: 'left', sortKey: 'trust_level' },
  { key: 'last_seen', label: 'Last Seen', align: 'left', sortKey: 'last_seen_at' },
];

const TRUST_BADGE_CLASS = {
  auto_high: 'bg-green-100 text-green-800',
  auto_medium: 'bg-yellow-100 text-yellow-800',
  auto_low: 'bg-orange-100 text-orange-800',
  human_confirmed: 'bg-blue-100 text-blue-800',
  human_overridden: 'bg-purple-100 text-purple-800',
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
}

function matchesNeedsAGlance(row) {
  return (
    (row.trust_level === 'auto_low' && (row.signal_count || 0) >= 5) ||
    row.llm_should_split === true
  );
}

export default function ClusterList() {
  const navigate = useNavigate();

  // Filter state
  const [trustLevel, setTrustLevel] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [denomination, setDenomination] = useState('');
  const [newThisWeek, setNewThisWeek] = useState(false);
  const [needsAGlance, setNeedsAGlance] = useState(false);

  // Sort + pagination state
  const [sortField, setSortField] = useState('signal_count');
  const [sortOrder, setSortOrder] = useState('desc');
  const [offset, setOffset] = useState(0);

  // Data state
  const [clusters, setClusters] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const params = {
          sort: sortField,
          order: sortOrder,
          limit: PAGE_SIZE,
          offset,
        };
        if (trustLevel) params.trust_level = trustLevel;
        if (stateFilter.trim()) params.state = stateFilter.trim();
        if (denomination.trim()) params.denomination = denomination.trim();
        if (newThisWeek) params.new_this_week = 'true';

        const res = await churchDemandAPI.clusters(params);
        if (cancelled) return;
        setClusters(res.data?.clusters || []);
        setTotal(res.data?.total || 0);
      } catch (err) {
        if (cancelled) return;
        setError(
          err?.response?.data?.error ||
            err?.message ||
            'Failed to load clusters'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [
    trustLevel,
    stateFilter,
    denomination,
    newThisWeek,
    sortField,
    sortOrder,
    offset,
  ]);

  // Reset offset when any backend filter changes (not when needsAGlance toggles — that's client-side)
  const applyFilterChange = (setter) => (value) => {
    setter(value);
    setOffset(0);
  };

  // Header click: clicking the active column toggles asc/desc; clicking any
  // other column makes it active and defaults to desc (matches initial state).
  const handleSortClick = (sortKey) => {
    if (sortField === sortKey) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(sortKey);
      setSortOrder('desc');
    }
    setOffset(0);
  };

  // Client-side filter — applied to the currently-loaded page
  const visibleClusters = needsAGlance
    ? clusters.filter(matchesNeedsAGlance)
    : clusters;

  const showingStart = total === 0 ? 0 : offset + 1;
  const showingEnd = Math.min(offset + PAGE_SIZE, total);
  const hasPrev = offset > 0;
  const hasNext = offset + PAGE_SIZE < total;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        {/* Trust level chips */}
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mr-2">
            Trust
          </span>
          {TRUST_LEVELS.map((t) => (
            <button
              key={t.value || 'all'}
              type="button"
              onClick={() => applyFilterChange(setTrustLevel)(t.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors min-h-[36px] ${
                trustLevel === t.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Text filters + toggles */}
        <div className="flex flex-wrap gap-3 items-center">
          <label className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              State
            </span>
            <input
              type="text"
              value={stateFilter}
              onChange={(e) => applyFilterChange(setStateFilter)(e.target.value)}
              placeholder="e.g. TX"
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-24 min-h-[36px]"
            />
          </label>
          <label className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Denomination
            </span>
            <input
              type="text"
              value={denomination}
              onChange={(e) => applyFilterChange(setDenomination)(e.target.value)}
              placeholder="e.g. baptist"
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-36 min-h-[36px]"
            />
          </label>
          <label className="flex items-center gap-2 ml-2">
            <input
              type="checkbox"
              checked={newThisWeek}
              onChange={(e) => applyFilterChange(setNewThisWeek)(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">New this week</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={needsAGlance}
              onChange={(e) => setNeedsAGlance(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">
              Needs a glance{' '}
              <span className="text-xs text-gray-400">(client-side; expand page size to see more)</span>
            </span>
          </label>
        </div>

      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl px-4 py-3 text-sm">
          <strong className="font-semibold">Failed to load:</strong> {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {COLUMNS.map((col) => {
                  const isActive = sortField === col.sortKey;
                  const alignClass = col.align === 'right' ? 'text-right' : 'text-left';
                  const flexJustify = col.align === 'right' ? 'justify-end' : 'justify-start';
                  return (
                    <th
                      key={col.key}
                      scope="col"
                      aria-sort={
                        isActive ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'
                      }
                      className={`px-4 py-3 ${alignClass} text-xs font-semibold uppercase tracking-wider`}
                    >
                      <button
                        type="button"
                        onClick={() => handleSortClick(col.sortKey)}
                        className={`group inline-flex items-center gap-1 ${flexJustify} ${
                          isActive ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <span>{col.label}</span>
                        {isActive ? (
                          sortOrder === 'desc' ? (
                            <ChevronDownIcon className="w-3.5 h-3.5" aria-hidden="true" />
                          ) : (
                            <ChevronUpIcon className="w-3.5 h-3.5" aria-hidden="true" />
                          )
                        ) : (
                          <ChevronUpDownIcon className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500" aria-hidden="true" />
                        )}
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                    Loading clusters…
                  </td>
                </tr>
              )}
              {!loading && visibleClusters.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                    No clusters match the current filters.
                  </td>
                </tr>
              )}
              {!loading &&
                visibleClusters.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => navigate(`/church-demand/clusters/${row.id}`)}
                    className="hover:bg-blue-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium max-w-xs truncate">
                      {row.canonical_name || row.name_slug || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {row.denomination || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {[row.city, row.state].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right tabular-nums">
                      {row.signal_count ?? 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right tabular-nums">
                      {row.unique_user_count ?? 0}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          TRUST_BADGE_CLASS[row.trust_level] || 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {row.trust_level || 'unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDate(row.last_seen_at)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>
          Showing {showingStart}–{showingEnd} of {total}
          {needsAGlance && visibleClusters.length !== clusters.length && (
            <span className="text-gray-400">
              {' '}
              · filtered to {visibleClusters.length} on this page
            </span>
          )}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={!hasPrev}
            className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 min-h-[36px]"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            Prev
          </button>
          <button
            type="button"
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={!hasNext}
            className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 min-h-[36px]"
          >
            Next
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
